import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncList, useLastKnownTotal, useStableValue, useTableState } from "@/ds";
import type { FilterValue } from "@/ds";
import { DEFAULT_ROLE_SORT, isRoleSortField, listRoles } from "@/modules/roles";
import type { Role, RoleSort, RoleSortField } from "@/modules/roles";
import {
  ROLES_ERROR_MESSAGE,
  ROLES_FORBIDDEN_MESSAGE,
  ROLES_PERMISSION_FILTER_KEY,
} from "../roles.constants";

interface ListaParams {
  busca: string;
  claims: string[];
  page: number;
  pageSize: number;
  sort: RoleSort;
}

const NO_CLAIMS: string[] = [];

/** Model da listagem: busca, permissões, página e ordenação no servidor; mudar um critério volta à página 1. */
export function useRolesLista() {
  const [search, setSearch] = useState("");
  const [rawClaims, setClaims] = useState<string[]>(NO_CLAIMS);
  const claims = useStableValue(rawClaims);
  const busca = search.trim();

  const { lastTotal, remember } = useLastKnownTotal();
  const tabela = useTableState<RoleSortField>({
    isSortField: isRoleSortField,
    total: lastTotal,
    resetKey: JSON.stringify([busca, claims]),
  });
  // Sem coluna escolhida a consulta segue a ordem padrão e nenhum cabeçalho mostra a seta.
  const appliedSort: RoleSort = tabela.sort ?? DEFAULT_ROLE_SORT;

  const lista = useAsyncList<ListaParams, Role>({
    fetcher: async (params, signal) => {
      const { roles, total } = await listRoles({
        pesquisa: params.busca,
        claims: params.claims,
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        signal,
      });
      return { items: roles, total };
    },
    params: {
      busca,
      claims,
      page: tabela.page + 1,
      pageSize: tabela.rows,
      sort: appliedSort,
    },
    debounceKey: busca,
    forbiddenMessage: ROLES_FORBIDDEN_MESSAGE,
    errorMessage: ROLES_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
  remember(lista.status, lista.total);

  return {
    search,
    busca,
    claims,
    appliedSort,
    hasSearch: busca !== "",
    hasFilters: claims.length > 0,
    items: lista.items,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    tabela,
    /** Sem espaço no começo (a busca não nasce de um espaço) e sem refazer o estado quando nada mudou. */
    buscar: (texto: string) => {
      const proximo = texto.trimStart();
      if (proximo !== search) setSearch(proximo);
    },
    clearSearch: () => setSearch(""),
    /** As permissões escolhidas no painel entram juntas na consulta (a regra E/OU é pendência da PO). */
    applyFilters: (value: FilterValue) =>
      setClaims(value[ROLES_PERMISSION_FILTER_KEY] ?? NO_CLAIMS),
    clearFilters: () => setClaims(NO_CLAIMS),
  };
}

export type UseRolesListaResult = ReturnType<typeof useRolesLista>;
